import _ from "lodash";
import moment from "moment";

const initialState = () => ({
  courses: {},
  modules: [],
  seminars: [],
  eventsOrdered: [],
  languageFilter: [],
  moduleFilter: [],
  seminarFilter: [],
  levelFilter: [],
  filterState: {
    moduleIds: [],
    seminarIds: [],
    levelIds: [],
    languageIds: [],
    dateFrom: null,
    dateTo: null,
  },
});

const actions = {
  /**
   * Setup data in the store after receiving them from the api
   */
  setData(data) {
    this.modules = data.modules.map((module) => {
      return {
        ...module,
        visible: true,
        courses: [],
      };
    });
    this.seminars = data.seminars.map((seminar) => {
      return {
        ...seminar,
        visible: true,
        courses: [],
      };
    });

    // put courses into dictionary, key is the id of the course
    // also add references to courses on this.modules
    this.courses = {};
    data.courses = data.courses.filter((course) => course.module);
    data.courses = data.courses.filter((course) => !course.hasSeminarSeminar);

    data.courses.map((course) => {
      let module = _.find(this.modules, { id: course.module });
      module.courses.push(course.id);
      this.courses[course.id] = {
        ...course,
        visible: true,
      };
    });

    // Order events by date and remove courses events
    // that are passed
    this.eventsOrdered = [];
    let indexKey = 0;

    // Make sure the filter is always set to false if not
    // explicitly set to true in the load Action
    if (!data.filterPastEvents) {
      data.filterPastEvents = false;
    }

    Object.values(this.courses).forEach((course) => {
      course.events.forEach((event) => {
        // Check to filter out past events or not
        // This has to be done as we use the same function for the planner and the CourseList
        if (
          data.filterPastEvents === false ||
          moment().isSameOrBefore(event.endDate)
        ) {
          this.eventsOrdered.push(
            Object.assign(
              {
                id: indexKey,
                courseId: course.id,
                moduleId: course.module,
                seminarIds: course.seminars,
                levelId: course.level.id,
                visible: true,
              },
              event,
            ),
          );
          indexKey++;
        }
      });
    });
    this.eventsOrdered.sort(
      (a, b) => moment(a.startDate) - moment(b.startDate),
    );

    // module filter
    this.moduleFilter = this.modules.map((module) => {
      return {
        id: module.id,
        name: module.name,
        checked: this.filterState.moduleIds.indexOf(module.id) !== -1,
        disabled: false,
        displayInOverview: module.displayInCourseOverview,
      };
    });

    // seminar filter
    this.seminarFilter = this.seminars.map((seminar) => {
      return {
        id: seminar.id,
        name: seminar.name,
        checked: this.filterState.seminarIds.indexOf(seminar.id) !== -1,
        disabled: false,
      };
    });

    // language filter
    let languages = data.courses.map((course) => course.languages);
    languages = _.uniqBy(_.flatten(languages), (language) => language.id);
    this.languageFilter = _.sortBy(
      languages.map((language) => {
        return {
          ...language,
          checked: this.filterState.languageIds.indexOf(language.id) !== -1,
          disabled: false,
        };
      }),
      "id",
    );

    // level filter
    let levels = data.courses.map((course) => course.level);
    levels = _.uniqBy(levels, (level) => level.id);
    this.levelFilter = levels.map((level) => {
      return {
        ...level,
        checked: this.filterState.levelIds.indexOf(level.id) !== -1,
        disabled: false,
      };
    });

    updateFilters(this, true);
  },

  toggleModuleFilter(moduleId) {
    const moduleFilter = _.find(this.moduleFilter, { id: moduleId });
    if (!moduleFilter.disabled) {
      moduleFilter.checked = !moduleFilter.checked;
    }
    updateFilters(this);
  },

  toggleSeminarFilter(seminarId) {
    const seminarFilter = _.find(this.seminarFilter, { id: seminarId });
    if (!seminarFilter.disabled) {
      seminarFilter.checked = !seminarFilter.checked;
    }
    updateFilters(this);
  },

  toggleLevelFilter(levelId) {
    const levelFilter = _.find(this.levelFilter, { id: levelId });
    if (!levelFilter.disabled) {
      levelFilter.checked = !levelFilter.checked;
    }
    updateFilters(this);
  },

  toggleLanguageFilter(languageId) {
    const languageFilter = _.find(this.languageFilter, { id: languageId });
    if (!languageFilter.disabled) {
      languageFilter.checked = !languageFilter.checked;
    }
    updateFilters(this);
  },
};

const getters = {
  languageFilterIds(state) {
    return state.languageFilter
      .filter((language) => language.checked === true)
      .map((language) => language.id);
  },
  moduleFilterIds(state) {
    return state.moduleFilter
      .filter((module) => module.checked === true)
      .map((module) => module.id);
  },
  seminarFilterIds(state) {
    return state.seminarFilter
      .filter((seminar) => seminar.checked === true)
      .map((seminar) => seminar.id);
  },
  levelFilterIds(state) {
    return state.levelFilter
      .filter((level) => level.checked === true)
      .map((level) => level.id);
  },
  hasActiveFilters() {
    return (
      this.moduleFilterIds.length !== 0 ||
      this.levelFilterIds.length !== 0 ||
      this.seminarFilterIds.length !== 0 ||
      this.languageFilterIds.length !== 0
    );
  },
};

/**
 * show/hide modules and courses
 * disable/enable filter items
 */
const updateFilters = (ctx, initialLoad = false) => {
  // This is very broken
  // Should for sure be refactored to use getters and not manipulate
  // the state directly
  let moduleFilterIds = ctx.moduleFilterIds;
  let seminarFilterIds = ctx.seminarFilterIds;
  let levelFilterIds = ctx.levelFilterIds;
  let languageFilterIds = ctx.languageFilterIds;
  let hasActiveFilters = ctx.hasActiveFilters;
  let dateFromFilter = ctx.dateFrom;
  let dateToFilter = ctx.dateTo;

  ctx.filterState.moduleIds = moduleFilterIds;
  ctx.filterState.seminarIds = seminarFilterIds;
  ctx.filterState.levelIds = levelFilterIds;
  ctx.filterState.languageIds = languageFilterIds;

  // show courses from all modules when none was selected
  if (moduleFilterIds.length === 0) {
    moduleFilterIds = ctx.moduleFilter.map((module) => module.id);
  }

  // show seminars from all modules when none was selected
  if (seminarFilterIds.length === 0) {
    seminarFilterIds = ctx.seminarFilter.map((seminar) => seminar.id);
  }

  // show courses from all levels when none was selected
  if (levelFilterIds.length === 0) {
    levelFilterIds = ctx.levelFilter.map((level) => level.id);
  }

  // show courses with all languages when none was selected
  if (languageFilterIds.length === 0) {
    languageFilterIds = ctx.languageFilter.map((language) => language.id);
  }

  let coursesFilteredByDate = _.uniq(
    _.values(ctx.eventsOrdered).filter((course) => {
      const eventStartDate = moment(course.startDate);
      const eventEndDate = moment(course.endDate);

      if (dateFromFilter && !eventStartDate.isSameOrAfter(dateFromFilter)) {
        return false;
      }

      if (dateToFilter && !eventEndDate.isSameOrBefore(dateToFilter)) {
        return false;
      }

      return true;
    }),
  ).map((course) => course.id);

  // ids of courses filtered by the module filter
  let coursesFilteredByModule = _.uniq(
    _.values(ctx.eventsOrdered)
      .filter((course) => moduleFilterIds.indexOf(course.moduleId) !== -1)
      .map((course) => course.id),
  );

  let coursesFilteredBySeminar = _.uniq(
    _.values(ctx.eventsOrdered).map((course) => course.id),
  );

  if (ctx.seminars.length !== seminarFilterIds.length) {
    coursesFilteredBySeminar = _.uniq(
      _.values(ctx.eventsOrdered)
        .filter(
          (course) =>
            seminarFilterIds.filter((n) => course.seminarIds.indexOf(n) !== -1)
              .length,
        )
        .map((course) => course.id),
    );
  }

  // hide courses which do not match the selected level
  Object.entries(ctx.courses).forEach((entry) => {
    const [index, course] = entry;
    course.visible = levelFilterIds.indexOf(course.level.id) !== -1;
    ctx.courses[index] = course;
  });

  // ids of courses filtered by the level filter
  let coursesFilteredByLevel = _.uniq(
    _.values(ctx.eventsOrdered)
      .filter((course) => levelFilterIds.indexOf(course.levelId) !== -1)
      .map((course) => course.id),
  );

  // ids of courses filtered by the language filter
  let coursesFilteredByLanguage = _.uniq(
    _.values(ctx.eventsOrdered)
      .filter((course) => languageFilterIds.includes(course.langId))
      .map((course) => course.id),
  );

  let visibleCourses = new Set(
    _.intersection(
      coursesFilteredByModule,
      coursesFilteredBySeminar,
      coursesFilteredByLevel,
      coursesFilteredByLanguage,
      coursesFilteredByDate,
    ),
  );

  let visibleModules = new Set();

  // show/hide courses
  const eventsOrderedVisibility = _.values(ctx.eventsOrdered).map((course) => {
    let visible = false;
    let stateCourse = ctx.courses[course.id];
    if (
      visibleCourses.has(course.id) ||
      (stateCourse && stateCourse.visibility)
    ) {
      visibleModules.add(course.moduleId);
      visible = true;
    }
    course.visible = visible;
    return course;
  });
  ctx.eventsOrdered = eventsOrderedVisibility;

  // show/hide modules
  ctx.modules.forEach((module, index) => {
    ctx.modules[index] = {
      ...module,
      visible: visibleModules.has(module.id) && module.displayInCourseOverview,
    };
  });

  // enable/disable filter dropdown items
  let selectableModuleIds = new Set(
    _.intersection(coursesFilteredByLevel, coursesFilteredByLanguage).map(
      (courseId) => {
        const event = ctx.eventsOrdered.find((item) => item.id === courseId);
        return event.moduleId;
      },
    ),
  );

  ctx.moduleFilter.forEach((moduleFilter) => {
    moduleFilter.disabled =
      !selectableModuleIds.has(moduleFilter.id) ||
      !moduleFilter.displayInOverview;
  });

  let selectableLevelIds = new Set(
    _.intersection(coursesFilteredByModule, coursesFilteredByLanguage).map(
      (courseId) => {
        const event = ctx.eventsOrdered.find((item) => item.id === courseId);
        return event.levelId;
      },
    ),
  );
  ctx.levelFilter.forEach((levelFilter) => {
    levelFilter.disabled = !selectableLevelIds.has(levelFilter.id);
  });

  let selectableLanguagesIds = new Set();
  ctx.eventsOrdered.forEach((item) => {
    if (
      _.intersection(coursesFilteredByLevel, coursesFilteredByModule).includes(
        item.id,
      )
    ) {
      selectableLanguagesIds.add(item.langId);
    }
  });

  ctx.languageFilter.forEach((languageFilter) => {
    languageFilter.disabled = !selectableLanguagesIds.has(languageFilter.id);
  });

  // open/close module sections if any filter is set
  if (!initialLoad) {
    if (hasActiveFilters) {
      ctx.filterState.openModules = ctx.modules.map((module) => module.id);
    } else {
      ctx.filterState.openModules = [];
    }
  }
};

export { initialState, actions, getters, updateFilters };
